import { AccessTimeFilled } from '@mui/icons-material'
import { Box, Card, CardContent, Stack, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import {
  CarrierVisitDirection,
  JourneyCategory,
  OrderResponseDto,
  OrderStatus,
} from '@planning/app/api'
import { ContainerDirectionChip } from '@planning/components/ContainerDirectionChip'
import { HoldEnforcementAlert } from '@planning/components/hold/HoldEnforcementAlert'
import { OrderAlerts } from '@planning/components/molecules/OrderAlerts'
import { useHoldEnforcement } from '@planning/utils/hold-enforcement'
import { canOrderProceed } from '@planning/utils/order-utils'
import { isValidityExpiredByNow } from '@planning/utils/release-validity-utils'
import { HoldEnforcementAlertWrapper } from '@planning/components/hold/HoldEnforcementAlertWrapper'


import { useTranslate } from '@tolgee/react'
import { ColoredLabel, HighlightedText, useMinimalsTheme } from '@tom-ui/ui'
import { formatDateTime } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { cloneElement, FC, ReactElement } from 'react'

interface Props {
  containerNumber: string
  direction: CarrierVisitDirection
  status: OrderStatus
  isOnTerminal: boolean
  highlight?: string
  onClick?: () => void
  children?: React.ReactNode
  releaseEnd?: string | null
  plannedYardLocation?: string
  isAssignedCraneNotToSelected?: boolean
  assignedCraneName?: string | null
  containerJourneyCategory?: JourneyCategory
  noPlannedEquipment?: boolean
  isApproved?: boolean
  order?: Partial<OrderResponseDto>
}

export const ContainerSearchResult: FC<Props> = observer(
  ({
    containerNumber,
    status,
    highlight,
    onClick,
    children,
    direction,
    releaseEnd,
    isOnTerminal,
    plannedYardLocation,
    isAssignedCraneNotToSelected,
    assignedCraneName,
    containerJourneyCategory,
    noPlannedEquipment,
    isApproved,
    order,
  }) => {
    const { tenantStore } = usePlanningStore()
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const { reaction, holdNames, isLoading } = useHoldEnforcement(order, 'Tallyman', direction)
    const isBlockedByHold = reaction === 'Block'
    const isValidState =
      !isBlockedByHold &&
      canOrderProceed({
        allowGateInWhenUnitNotOnTerminal: tenantStore.allowGateInWhenUnitNotOnTerminal,
        direction,
        hasBlockingHold: isBlockedByHold,
        isOnTerminal,
        isReleaseExpired: isValidityExpiredByNow(releaseEnd),
        skipReleaseOrder: tenantStore.skipReleaseOrder,
        noPlannedEquipment,
        isApproved,
      })


    const releaseValidityDate = dayjs(releaseEnd)

    return (
      <Card
        sx={{
          mb: '1rem',
        }}
        onClick={() => {
          if (isValidState) onClick?.()
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant='h3'>
              <HighlightedText highlight={highlight}>{containerNumber}</HighlightedText>
            </Typography>
            <Stack direction='row' alignItems='center' gap={theme.customSpacing.s}>
              {direction && (
                <ContainerDirectionChip
                  direction={direction}
                  label={
                    direction === 'Inbound' ? t('dropOff', 'Drop Off') : t('pickUp', 'Pick Up')
                  }
                />
              )}
              {direction === CarrierVisitDirection.Outbound && releaseEnd && (
                <ColoredLabel
                  color={isValidityExpiredByNow(releaseEnd) ? 'error' : 'warning'}
                  startIcon={<AccessTimeFilled fontSize='small' />}
                  label={
                    isValidityExpiredByNow(releaseEnd)
                      ? t('expired', 'Expired')
                      : `${t('validTill', 'Valid till')} ${formatDateTime(releaseValidityDate)}`
                  }
                />
              )}
            </Stack>
          </Box>
          {containerJourneyCategory &&
            (containerJourneyCategory === JourneyCategory.Restow ||
              containerJourneyCategory === JourneyCategory.Transshipment ||
              containerJourneyCategory === JourneyCategory.Overlanded) && (
              <Typography variant='body2' color='textSecondary'>
                {t('containerJourneyCategory', '{category} container', {
                  category: t(containerJourneyCategory),
                })}
              </Typography>
            )}

          <OrderAlerts
            status={status}
            order={order}
            context="Tallyman"
            direction={direction}
            isOnTerminal={isOnTerminal}
            releaseEnd={releaseEnd}
            plannedYardLocation={plannedYardLocation}
            incorrectAssignedCraneName={
              isAssignedCraneNotToSelected ? assignedCraneName : undefined
            }
            noPlannedEquipment={noPlannedEquipment}
            isApproved={isApproved}
          />


          {children &&
            cloneElement(children as ReactElement, {
              disabled: !isValidState,
            })}
        </CardContent>
      </Card>
    )
  },
)
